import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import needle from 'needle';
import { from } from 'rxjs';
import { projectConfig } from '../../config.js';
import type { Student } from '../users/Student.js';
import { ChancenEventlog } from './ChancenEventlog.js';

export enum MailEventLogType {
  TOKEN_REGISTER = 'token_register',
  MITARBEITER_REGISTER = 'mitarbeiter_register',
  CONTACT_PERSON_REGISTER = 'contact_person_register',
  REGISTER = 'register',
  WORKSHOP_ATTENDANCE_REGISTER = 'workshop_attendance_register',
  WORKSHOP_STORNO = 'workshop_storno',
  WORKSHOP_INFO = 'workshop_info',
}

@Entity()
export class MailEventWithUser extends AdornisEntity {
  static override _class = 'MailEventWithUser';

  static override resolveID(entity: any): string {
    return A.getGloballyUniqueID();
  }

  @Field(type => String) vorname?: string;
  @Field(type => String) nachname?: string;
  @Field(type => String) userID!: string;
  @Field(type => String) email!: string;

  @Field(type => String) info!: string;
}

@Entity()
export class MailEventLog extends ChancenEventlog {
  static override _class = 'MailEventLog';

  @validate(nonOptional())
  @Field(type => String)
  mailType!: MailEventLogType;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean)
  successful!: boolean;

  @validate(nonOptional())
  @Field(type => String)
  emailAddress!: string;

  @Field(type => String)
  sendMailError?: string;

  @Field(type => String, {
    // resolve: function (this: MailEventLog) {
    //   return async () => {
    //     if (this.postmarkError !== null && this.postmarkError !== undefined) return this.postmarkError;
    //     return MailEventLog._requestPostmark({ emailFilter: this.emailAddress, fromdate: this.createdAt });
    //   };
    // },
  })
  postmarkError?: string;

  constructor(doc: PartialEntityData<MailEventLog>) {
    super(doc);
  }

  @Query(type => Int)
  static getSentRegistrationMailsCount() {
    return async () => {
      const result = await this.getSuccessfulEventLogs([
        MailEventLogType.TOKEN_REGISTER,
        MailEventLogType.MITARBEITER_REGISTER,
      ]);

      return result.length;
    };
  }

  // just a subscription to match data-view api
  @Subscription(type => [MailEventWithUser])
  static subscribeSentRegistrationMailsStudents(
    @Arg('skip', type => Int) skip: number = 0,
    @Arg('limit', type => Int) limit: number = 100,
  ) {
    return (gqlFields: BaseQLSelectionSet<MailEventWithUser>) => {
      return from(
        new Promise(async resolve => {
          const result = await this.getSuccessfulEventLogs(
            [MailEventLogType.TOKEN_REGISTER, MailEventLogType.MITARBEITER_REGISTER],
            skip,
            limit,
          );

          resolve(
            result.map(resultItem => {
              return new MailEventWithUser({
                vorname: resultItem.user.vorname,
                nachname: resultItem.user.nachname,
                email: resultItem.user.email,
                userID: resultItem.user._id,
                info: DateTime.fromJSDate(resultItem.createdAt).toFormat('dd.LL.yyyy'),
              });
            }),
          );
        }),
      );
    };
  }

  // just a subscription to match data-view api
  @Subscription(type => [MailEventWithUser])
  static subscribeFailedRegistrationMailsStudents(
    @Arg('skip', type => Int) skip: number = 0,
    @Arg('limit', type => Int) limit: number = 100,
  ) {
    return (gqlFields: BaseQLSelectionSet<MailEventWithUser>) => {
      return from(
        new Promise(async resolve => {
          const result = await this.getFailedEventLogs(
            [MailEventLogType.TOKEN_REGISTER, MailEventLogType.MITARBEITER_REGISTER],
            skip,
            limit,
          );

          resolve(
            result.map(resultItem => {
              return new MailEventWithUser({
                vorname: resultItem.user.vorname,
                nachname: resultItem.user.nachname,
                email: resultItem.email,
                userID: resultItem.user._id,
                info: resultItem.sendMailError || resultItem.postmarkError,
              });
            }),
          );
        }),
      );
    };
  }

  private static async _getFailedRegistrationMails() {
    await this.syncMailDataWithPostmark();
    return this.getFailedEventLogs([MailEventLogType.TOKEN_REGISTER, MailEventLogType.MITARBEITER_REGISTER]);
  }

  @Query(type => Int)
  static getFailedRegistrationMailsCount() {
    return async () => {
      const allFailedMails = await this._getFailedRegistrationMails();
      return allFailedMails.length;
    };
  }

  private static async _getNoRegistrationmailAggregationHelper() {
    return [
      {
        $match: {
          _class: { $in: ['Student', 'ExternerMitarbeiter'] },
          $or: [
            { 'kuendigung.austrittsdatum': { $exists: false } },
            { 'kuendigung.austrittsdatum': null },
            { 'kuendigung.austrittsdatum': { $gte: new Date() } },
          ],
        },
      },
      { $project: { email: 1, vorname: 1, nachname: 1 } },
      {
        $lookup: {
          from: 'eventlog',
          as: 'maileventlog',
          let: { email: '$email' },
          pipeline: [
            { $match: { $expr: { $eq: ['$emailAddress', '$$email'] } } },
            {
              $match: {
                _class: 'MailEventLog',
                mailType: { $in: [MailEventLogType.TOKEN_REGISTER, MailEventLogType.MITARBEITER_REGISTER] },
              },
            },
            { $project: { _id: 1 } },
          ],
        },
      },
      { $addFields: { numEventLogs: { $size: '$maileventlog' } } },
      { $match: { numEventLogs: 0 } },
    ];
  }

  private static async _getNoRegistrationMailsCount() {
    await this.syncMailDataWithPostmark();
    const { ChancenwerkUser } = await import('@chancenwerk/master-data-management/users/entities/ChancenwerkUser');

    const userCol = await getRawCollection(ChancenwerkUser._collectionName);
    return (
      await userCol
        .aggregate([...(await this._getNoRegistrationmailAggregationHelper()), { $count: 'count' }])
        .toArray()
    )[0]!.count;
  }

  @Query(type => Int)
  static getNoRegistrationMailsCount() {
    return async () => {
      return this._getNoRegistrationMailsCount();
    };
  }

  // just a subscription to match data-view api
  @Subscription(type => [MailEventWithUser])
  static subscribeNoRegistrationMailsStudents(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
  ) {
    return (gqlFields: BaseQLSelectionSet<MailEventWithUser>) => {
      return from(
        new Promise(async resolve => {
          const users = await this.getNoMailSentEventLogs(skip, limit);

          resolve(
            users.map(user => {
              return new MailEventWithUser({
                vorname: user.vorname,
                nachname: user.nachname,
                userID: user._id,
                email: user.email,
                info: 'Es wurde noch keine Registrierungsmail versandt',
              });
            }),
          );
        }),
      );
    };
  }

  static async getNoMailSentEventLogs(skip?: number, limit?: number) {
    if (!A.isServer) throw new Error('Cannot call on client');

    const { ChancenwerkUser } = await import('@chancenwerk/master-data-management/users/entities/ChancenwerkUser');
    const usercol = await getRawCollection(ChancenwerkUser._collectionName);
    return usercol
      .aggregate([
        ...(await this._getNoRegistrationmailAggregationHelper()),
        ...(skip ? [{ $skip: skip }] : []),
        ...(limit ? [{ $limit: limit }] : []),
      ])
      .toArray();
  }

  static async getSuccessfulEventLogs(applicableMailTypes: MailEventLogType[], skip?: number, limit?: number) {
    if (!A.isServer) throw new Error('Cannot call on client');

    await this.syncMailDataWithPostmark();
    return this._getEventLogs(applicableMailTypes, true, skip, limit);
  }

  static async getFailedEventLogs(applicableMailTypes: MailEventLogType[], skip?: number, limit?: number) {
    if (!A.isServer) throw new Error('Cannot call on client');

    await this.syncMailDataWithPostmark();
    return this._getEventLogs(applicableMailTypes, false, skip, limit);
  }

  private static async _getEventLogs(
    applicableMailTypes: MailEventLogType[],
    successful?: boolean,
    skip?: number,
    limit?: number,
  ) {
    const coll = await getRawCollection(this._collectionName);
    return (await coll
      .aggregate([
        {
          $match: {
            _class: this._class,
            ...(typeof successful === 'boolean' ? { successful } : {}),
            mailType: {
              $in: applicableMailTypes,
            },
          },
        },
        { $sort: { createdAt: -1 } },
        {
          $group: {
            _id: '$emailAddress',
            userID: { $first: '$userID' },
            createdAt: { $first: '$createdAt' },
            sendMailError: { $first: '$sendMailError' },
            postmarkError: { $first: '$postmarkError' },
          },
        },
        { $project: { _id: 0, email: '$_id', userID: 1, createdAt: 1, sendMailError: 1, postmarkError: 1 } },
        { $lookup: { from: 'adornisuser', as: 'user', localField: 'userID', foreignField: '_id' } },
        { $unwind: '$user' },
        {
          $match: {
            $or: [
              { 'user.kuendigung.austrittsdatum': { $exists: false } },
              { 'user.kuendigung.austrittsdatum': null },
              { 'user.kuendigung.austrittsdatum': { $gte: new Date() } },
            ],
          },
        },
        ...(skip ? [{ $skip: skip }] : []),
        ...(limit ? [{ $limit: limit }] : []),
      ])
      .toArray()) as Array<{
      email: string;
      userID: string;
      createdAt: Date;
      sendMailError?: string;
      postmarkError?: string;
      user: EntityData<Student>;
    }>;
  }

  static syncMailPromise: Maybe<Promise<void>>;
  static async syncMailDataWithPostmark() {
    if (this.syncMailPromise) return this.syncMailPromise;
    this.syncMailPromise = new Promise<void>(async resolve => {
      const { ChancenwerkUser } = await import('@chancenwerk/master-data-management/users/entities/ChancenwerkUser');
      const usersCol = await getRawCollection(ChancenwerkUser._collectionName);
      const avoidDoubleEventLogAggregationSteps = (successful: boolean) => [
        {
          $lookup: {
            from: 'eventlog',
            as: 'mailEventLog',
            let: { userID: '$_id' },
            pipeline: [
              { $match: { $expr: { $and: [{ $eq: ['$_class', 'MailEventLog'] }, { $eq: ['$userID', '$$userID'] }] } } },
              { $sort: { createdAt: -1 } },
              {
                $group: {
                  _id: '$userID',
                  successful: { $first: '$successful' },
                },
              },
              { $match: { successful } },
            ],
          },
        },
        { $addFields: { mailEventlogSize: { $size: '$mailEventLog' } } },
        { $match: { mailEventlogSize: 0 } },
      ];
      const alreadyRegisteredUsersMustHaveGottenAnEmail = await usersCol
        .aggregate([
          {
            $match: {
              $or: [{ emailConfirmed: true }, { 'joinInformation.campusRegisterDataSend': true }],
              $and: [{ email: { $ne: '' } }, { email: { $ne: null } }],
            },
          },
          ...avoidDoubleEventLogAggregationSteps(true),
        ])
        .toArray();

      await Promise.all(
        alreadyRegisteredUsersMustHaveGottenAnEmail
          .filter(user => user.email)
          .map(user =>
            new MailEventLog({
              userID: user._id,
              mailType:
                {
                  Student: MailEventLogType.TOKEN_REGISTER,
                  Mitarbeiter: MailEventLogType.MITARBEITER_REGISTER,
                  ExternerMitarbeiter: MailEventLogType.MITARBEITER_REGISTER,
                  VerwaltungsMitarbeiter: MailEventLogType.MITARBEITER_REGISTER,
                  ExternalContactPerson: MailEventLogType.CONTACT_PERSON_REGISTER,
                }[user._class] ?? MailEventLogType.REGISTER,
              successful: true,
              emailAddress: user.email,
            }).create(),
          ),
      );

      // successful mails
      const postmarkSentMails = await needle(
        'get',
        `https://api.postmarkapp.com/messages/outbound?${new URLSearchParams({
          offset: '0',
          count: '500',
          subject: 'Willkommen auf dem ChancenCampus',
          status: 'sent',
          fromdate: '2021-01-01T12:00:00',
          todate: '2023-07-26T12:00:00',
        }).toString()}`,
        {
          headers: {
            Accept: 'application/json',
            'X-Postmark-Server-Token': projectConfig.get('POSTMARK_SERVER_TOKEN'),
          },
        },
      );

      if (postmarkSentMails.body?.Messages && postmarkSentMails.body.TotalCount) {
        const successfulMailArray = postmarkSentMails.body.Messages.flatMap(message => message.To.map(to => to.Email));
        logger.info({ successfulMails: successfulMailArray.length }, 'Erfolgreicher Mailversand von Postmark');
        const postmarkSuccessfulMailSends = await usersCol
          .aggregate([
            { $match: { email: { $in: successfulMailArray } } },
            ...avoidDoubleEventLogAggregationSteps(true),
          ])
          .toArray();

        await Promise.all(
          postmarkSuccessfulMailSends.map(user => {
            const postmarkMessage = postmarkSentMails.body.Messages.find(message =>
              message.To.map(to => to.Email).includes(user.email),
            );
            return new MailEventLog({
              userID: user._id,
              mailType:
                {
                  Student: MailEventLogType.TOKEN_REGISTER,
                  Mitarbeiter: MailEventLogType.MITARBEITER_REGISTER,
                  ExternerMitarbeiter: MailEventLogType.MITARBEITER_REGISTER,
                  VerwaltungsMitarbeiter: MailEventLogType.MITARBEITER_REGISTER,
                  ExternalContactPerson: MailEventLogType.CONTACT_PERSON_REGISTER,
                }[user._class] ?? MailEventLogType.REGISTER,
              successful: true,
              emailAddress: user.email,
              ...(postmarkMessage ? { createdAt: new Date(postmarkMessage.ReceivedAt) } : {}),
            }).create();
          }),
        );
      }

      // Hard Bounced Mails
      const postmarkBouncedMails = await needle(
        'get',
        `https://api.postmarkapp.com/bounces?${new URLSearchParams({
          offset: '0',
          count: '500',
          type: 'HardBounce',
          fromdate: '2021-01-01T12:00:00',
          inactive: 'true',
        }).toString()}`,
        {
          headers: {
            Accept: 'application/json',
            'X-Postmark-Server-Token': projectConfig.get('POSTMARK_SERVER_TOKEN'),
          },
        },
      );

      if (postmarkBouncedMails.body?.Bounces && postmarkBouncedMails.body.TotalCount) {
        const bouncedMailArray = postmarkBouncedMails.body.Bounces.map(message => message.Email);
        logger.info({ successfulMails: bouncedMailArray.length }, 'Erfolgreicher Mailversand von Postmark');
        const postmarkBouncedMailSends = await usersCol
          .aggregate([{ $match: { email: { $in: bouncedMailArray } } }, ...avoidDoubleEventLogAggregationSteps(false)])
          .toArray();

        await Promise.all(
          postmarkBouncedMailSends.map(user => {
            const postmarkMessage = postmarkBouncedMails.body.Bounces.find(message => message.Email === user.email);
            return new MailEventLog({
              userID: user._id,
              mailType:
                {
                  Student: MailEventLogType.TOKEN_REGISTER,
                  Mitarbeiter: MailEventLogType.MITARBEITER_REGISTER,
                  ExternerMitarbeiter: MailEventLogType.MITARBEITER_REGISTER,
                  VerwaltungsMitarbeiter: MailEventLogType.MITARBEITER_REGISTER,
                  ExternalContactPerson: MailEventLogType.CONTACT_PERSON_REGISTER,
                }[user._class] ?? MailEventLogType.REGISTER,
              successful: false,
              emailAddress: user.email,
              ...(postmarkMessage
                ? { createdAt: new Date(postmarkMessage.BouncedAt), postmarkError: postmarkMessage.Description }
                : {}),
            }).create();
          }),
        );
      }

      this.syncMailPromise = null;
      resolve();
    });
  }
}

if (A.isServer) {
  getRawCollection(MailEventLog._collectionName).then(coll => coll.createIndex({ emailAddress: 1 }));
}
